import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Plus, FolderOpen, Send, Trash2, Eye, EyeOff, FileText, Globe, BarChart3 } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import InputMonto from '../../components/InputMonto';
import SelectorAviso from '../../components/SelectorAviso';
import ModalReporte from './ModalReporte';
import { money, fecha, hoy, Title, Badge, Empty, ESTADO_COBRO } from './ui';

const TIPOS_MOV = [
  { tipo: 'mantenimiento', label: 'Mantenimiento' },
  { tipo: 'reporte', label: 'Reporte' },
  { tipo: 'ajuste', label: 'Ajuste realizado' },
  { tipo: 'avance', label: 'Novedad' },
  { tipo: 'nota', label: 'Nota interna' },
];
const PLATAFORMAS = ['Google Ads', 'Meta Ads', 'Mailing', 'TikTok Ads', 'LinkedIn Ads'];
const MESES_LARGO = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
const periodoLabel = (p) => { const [a, m] = String(p || '').split('-'); return `${MESES_LARGO[Number(m) - 1] || ''} ${a || ''}`; };
const MESES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

export default function AgenciaServicio() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [form, setForm] = useState({});
  const [mov, setMov] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [reportes, setReportes] = useState([]);
  const [reporte, setReporte] = useState(null);

  const cargar = useCallback(() => {
    api.get('/agency/servicios/' + id).then(r => { setD(r.data); setForm(r.data.servicio); }).catch(() => toast.error('No se pudo cargar'));
    api.get('/agency/reportes-campana', { params: { contrato_id: id } }).then(r => setReportes(r.data)).catch(() => {});
  }, [id]);
  useEffect(() => { cargar(); }, [cargar]);
  if (!d) return <div style={{ color: 'var(--text-muted)', padding: 40 }}>Cargando...</div>;

  const s = d.servicio;
  const set = (campo) => (ev) => setForm({ ...form, [campo]: ev.target.value });
  const esAnual = form.tipo === 'anual';

  const guardar = async () => {
    setGuardando(true);
    try {
      await api.put('/agency/contratos/' + id, {
        nombre: form.nombre, monto: form.monto, estado: form.estado, dia_cobro: form.dia_cobro || '',
        mes_renovacion: form.mes_renovacion || '', proximo_vencimiento: (form.proximo_vencimiento || '').slice(0, 10) || '',
        drive_url: form.drive_url || '', web_url: form.web_url || '', panel_url: form.panel_url || '',
        plataformas: form.plataformas || '',
        visible_cliente: form.visible_cliente ? 1 : 0, notas: form.notas || '',
      });
      toast.success('Guardado'); cargar();
    } catch { toast.error('No se pudo guardar'); } finally { setGuardando(false); }
  };

  const guardarMov = async (ev) => {
    ev.preventDefault();
    try {
      if (mov.id) await api.put('/agency/movimientos/' + mov.id, mov);
      else {
        const { data } = await api.post('/agency/movimientos', { ...mov, entidad: 'servicio', entidad_id: id });
        if (data.aviso && data.aviso.error) toast.error(data.aviso.error);
      }
      toast.success('Guardado'); setMov(null); cargar();
    } catch (err) { toast.error(err.response?.data?.error || 'No se pudo guardar'); }
  };
  const enviarMov = async (m) => {
    if (!window.confirm(`Enviarle "${m.titulo}" por mail al cliente?`)) return;
    try { const { data } = await api.post(`/agency/movimientos/${m.id}/enviar`); toast.success('Enviado a ' + data.destinos.join(', ')); cargar(); }
    catch (err) { toast.error(err.response?.data?.error || 'No se pudo enviar'); }
  };
  const borrarMov = async (m) => {
    if (!window.confirm('¿Borrar este movimiento?')) return;
    try { await api.delete('/agency/movimientos/' + m.id); cargar(); } catch { toast.error('Error'); }
  };
  const verClienteMov = async (m) => {
    try { await api.put('/agency/movimientos/' + m.id, { visible_cliente: m.visible_cliente ? 0 : 1 }); cargar(); } catch { toast.error('Error'); }
  };

  const card = { marginBottom: 18 };
  const td = { padding: '9px 8px', borderTop: '1px solid var(--border)', fontSize: 13 };
  const tipoLabel = s.tipo === 'abono' ? 'Abono mensual' : s.tipo === 'anual' ? 'Renovación anual' : 'Pago único';

  return (
    <div style={{ maxWidth: 900 }}>
      <button className="btn btn-ghost btn-sm" onClick={() => navigate('/agencia/clientes/' + s.cliente_id)} style={{ marginBottom: 12 }}>
        <ArrowLeft size={15} /> {s.cliente}
      </button>

      <Title right={<Badge label={tipoLabel} color="var(--accent)" bg="var(--accent-glow)" />}>{s.nombre || s.servicio}</Title>

      <div className="card" style={card}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 12 }}>
          <div className="form-group"><label>Nombre</label><input value={form.nombre || ''} onChange={set('nombre')} /></div>
          <div className="form-group"><label>Importe</label><InputMonto value={form.monto ?? ''} onChange={v => setForm({ ...form, monto: v })} /></div>
          <div className="form-group"><label>Estado</label>
            <select value={form.estado || 'activo'} onChange={set('estado')}>
              <option value="activo">Activo</option><option value="pausado">Pausado</option><option value="baja">Dado de baja</option>
            </select></div>
          {esAnual ? (<>
            <div className="form-group"><label>Mes de renovación</label>
              <select value={form.mes_renovacion || ''} onChange={set('mes_renovacion')}>
                <option value="">—</option>{MESES.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
              </select></div>
            <div className="form-group"><label>Próximo vencimiento</label>
              <input type="date" value={(form.proximo_vencimiento || '').slice(0, 10)} onChange={set('proximo_vencimiento')} /></div>
          </>) : (
            <div className="form-group"><label>Día de cobro</label>
              <input type="number" min="1" max="31" value={form.dia_cobro || ''} onChange={set('dia_cobro')} /></div>
          )}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12 }}>
          <div className="form-group"><label>Sitio web del cliente</label>
            <input value={form.web_url || ''} onChange={set('web_url')} placeholder="https://" /></div>
          <div className="form-group"><label>Carpeta de Drive</label>
            <input value={form.drive_url || ''} onChange={set('drive_url')} placeholder="Facturas, reportes, documentación" /></div>
          <div className="form-group"><label>Panel de datos (opcional)</label>
            <input value={form.panel_url || ''} onChange={set('panel_url')} placeholder="Looker Studio u otro reporte compartido" />
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>Si lo cargás, el cliente ve el panel dentro de su portal.</div></div>
        </div>

        <div className="form-group"><label>Plataformas que incluye</label>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {PLATAFORMAS.map(pl => {
              const lista = (form.plataformas || '').split(',').map(x => x.trim()).filter(Boolean);
              const activa = lista.includes(pl);
              return (
                <button key={pl} type="button"
                  onClick={() => setForm({ ...form, plataformas: (activa ? lista.filter(x => x !== pl) : [...lista, pl]).join(', ') })}
                  style={{ padding: '6px 12px', borderRadius: 999, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                    border: '1px solid ' + (activa ? 'var(--accent)' : 'var(--border)'),
                    background: activa ? 'var(--accent)' : 'transparent', color: activa ? '#fff' : 'var(--text-secondary)' }}>{pl}</button>
              );
            })}
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>
            Un servicio de pauta puede incluir varias plataformas: el cliente lo ve como un único servicio.
          </div>
        </div>

        <div className="form-group"><label>Notas internas</label>
          <textarea rows={2} value={form.notas || ''} onChange={set('notas')} style={{ fontFamily: 'inherit', fontSize: 13 }} /></div>

        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, marginBottom: 14, cursor: 'pointer' }}>
          <input type="checkbox" checked={form.visible_cliente === undefined ? true : !!form.visible_cliente}
            onChange={ev => setForm({ ...form, visible_cliente: ev.target.checked ? 1 : 0 })} style={{ width: 16, height: 16 }} />
          Visible en el portal del cliente
        </label>

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <button className="btn btn-primary" onClick={guardar} disabled={guardando}><Save size={16} /> {guardando ? 'Guardando...' : 'Guardar'}</button>
          {form.web_url && <a className="btn btn-secondary" href={form.web_url} target="_blank" rel="noopener noreferrer"><Globe size={16} /> Ver sitio</a>}
          {form.drive_url && <a className="btn btn-secondary" href={form.drive_url} target="_blank" rel="noopener noreferrer"><FolderOpen size={16} /> Drive</a>}
          {form.panel_url && <a className="btn btn-secondary" href={form.panel_url} target="_blank" rel="noopener noreferrer"><BarChart3 size={16} /> Panel</a>}
        </div>
      </div>

      <div className="card" style={card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4, gap: 10, flexWrap: 'wrap' }}>
          <h3 style={{ fontSize: 16, fontWeight: 700 }}>Actividad del servicio</h3>
          <button className="btn btn-secondary btn-sm" onClick={() => setMov({ tipo: 'mantenimiento', titulo: '', detalle: '', fecha: hoy(), visible_cliente: 1 })}>
            <Plus size={14} /> Agregar
          </button>
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>
          Mantenimientos, ajustes, reportes: lo que quede visible lo ve el cliente en su portal.
        </div>
        {d.movimientos.length ? d.movimientos.map(m => (
          <div key={m.id} style={{ borderTop: '1px solid var(--border)', padding: '12px 0', opacity: m.visible_cliente ? 1 : 0.6 }}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap' }}>
              <span style={{ fontSize: 11, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{fecha(m.fecha)}</span>
              <span style={{ fontWeight: 600, fontSize: 14 }}>{m.titulo}</span>
              <Badge label={(TIPOS_MOV.find(t => t.tipo === m.tipo) || {}).label || m.tipo} />
              <div style={{ marginLeft: 'auto', display: 'flex', gap: 2 }}>
                <button className="btn btn-ghost btn-sm" onClick={() => verClienteMov(m)} title={m.visible_cliente ? 'Ocultar al cliente' : 'Mostrar al cliente'}>
                  {m.visible_cliente ? <Eye size={14} /> : <EyeOff size={14} />}
                </button>
                <button className="btn btn-ghost btn-sm" onClick={() => setMov({ ...m, fecha: (m.fecha || '').slice(0, 10) })} title="Editar"><FileText size={14} /></button>
                <button className="btn btn-ghost btn-sm" onClick={() => enviarMov(m)} title="Enviar por mail"><Send size={14} /></button>
                <button className="btn btn-ghost btn-sm" onClick={() => borrarMov(m)} title="Borrar"><Trash2 size={14} /></button>
              </div>
            </div>
            {m.detalle && <div style={{ fontSize: 13, color: 'var(--text-secondary)', whiteSpace: 'pre-line', marginTop: 6, lineHeight: 1.6 }}>{m.detalle}</div>}
          </div>
        )) : <Empty>Todavía no cargaste actividad</Empty>}
      </div>

      <div className="card" style={card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4, gap: 10, flexWrap: 'wrap' }}>
          <h3 style={{ fontSize: 16, fontWeight: 700 }}>Reportes de campañas</h3>
          <button className="btn btn-secondary btn-sm" onClick={() => setReporte({})}><Plus size={14} /> Cargar reporte</button>
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>
          El informe del mes y sus números. El agente de reportes también los puede subir solo por API.
        </div>
        {reportes.length ? (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>{reportes.map(r => (
              <tr key={r.id} style={{ cursor: 'pointer' }} onClick={() => setReporte(r)}>
                <td style={{ ...td, fontWeight: 600, textTransform: 'capitalize' }}>{periodoLabel(r.periodo)}</td>
                <td style={{ ...td, fontSize: 12, color: 'var(--text-muted)' }}>
                  {r.totales.inversion ? money(r.totales.inversion) : '—'}
                  {r.totales.resultados ? ` · ${r.totales.resultados} resultados` : ''}
                </td>
                <td style={td}>{r.publicado
                  ? <Badge label={r.enviado_at ? 'Publicado y enviado' : 'Publicado'} color="#22c55e" bg="rgba(34,197,94,0.15)" />
                  : <Badge label="Borrador" color="#64748b" bg="rgba(100,116,139,0.15)" />}</td>
                <td style={{ ...td, textAlign: 'right' }}>{r.documento_url
                  ? <a href={r.documento_url} target="_blank" rel="noopener noreferrer" onClick={ev => ev.stopPropagation()}><FileText size={15} /></a>
                  : <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>sin informe</span>}</td>
              </tr>
            ))}</tbody>
          </table>
        ) : <Empty>Todavía no cargaste reportes de este servicio</Empty>}
      </div>

      <div className="card">
        <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 10 }}>Cobros de este servicio</h3>
        {d.cobros.length ? (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>{d.cobros.map(c => {
              const ec = ESTADO_COBRO[c.estado] || {};
              return (
                <tr key={c.id} style={{ opacity: c.habilitado ? 1 : 0.6 }}>
                  <td style={td}>{c.concepto}</td>
                  <td style={{ ...td, whiteSpace: 'nowrap', fontSize: 12, color: 'var(--text-muted)' }}>{fecha(c.vencimiento)}</td>
                  <td style={{ ...td, textAlign: 'right', fontWeight: 600 }}>{money(c.monto)}</td>
                  <td style={td}><Badge label={ec.label} color={ec.color} bg={ec.bg} /></td>
                </tr>
              );
            })}</tbody>
          </table>
        ) : <Empty>Sin cobros todavía</Empty>}
      </div>

      {reporte && (
        <ModalReporte reporte={reporte.id ? reporte : null} cliente_id={s.cliente_id} contrato_id={s.id}
          plataformas={(form.plataformas || '').split(',').map(x => x.trim()).filter(Boolean)}
          onClose={() => setReporte(null)} onListo={() => { setReporte(null); cargar(); }} />
      )}

      {mov && (
        <div className="modal-overlay" onClick={() => setMov(null)}>
          <div className="modal" onClick={ev => ev.stopPropagation()} style={{ maxWidth: 520 }}>
            <h2>{mov.id ? 'Editar' : 'Nueva actividad'}</h2>
            <form onSubmit={guardarMov}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="form-group"><label>Tipo</label>
                  <select value={mov.tipo} onChange={ev => setMov({ ...mov, tipo: ev.target.value })}>
                    {TIPOS_MOV.map(t => <option key={t.tipo} value={t.tipo}>{t.label}</option>)}
                  </select></div>
                <div className="form-group"><label>Fecha</label>
                  <input type="date" value={mov.fecha || hoy()} onChange={ev => setMov({ ...mov, fecha: ev.target.value })} /></div>
              </div>
              <div className="form-group"><label>Título</label>
                <input value={mov.titulo} onChange={ev => setMov({ ...mov, titulo: ev.target.value })} required autoFocus placeholder="Actualización de WordPress y plugins" /></div>
              <div className="form-group"><label>Detalle</label>
                <textarea rows={4} value={mov.detalle || ''} onChange={ev => setMov({ ...mov, detalle: ev.target.value })} style={{ fontFamily: 'inherit', fontSize: 13 }} /></div>
              <div className="form-group"><label>Enlace (reporte, factura, documento)</label>
                <input value={mov.archivo_url || ''} onChange={ev => setMov({ ...mov, archivo_url: ev.target.value })} placeholder="https://" /></div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, marginBottom: 14, cursor: 'pointer' }}>
                <input type="checkbox" checked={mov.visible_cliente !== 0} onChange={ev => setMov({ ...mov, visible_cliente: ev.target.checked ? 1 : 0 })} style={{ width: 16, height: 16 }} />
                Que lo vea el cliente en su portal
              </label>
              {mov.visible_cliente !== 0 && !mov.id && (
                <SelectorAviso value={mov.notificar || 'portal'} onChange={c => setMov({ ...mov, notificar: c })} />
              )}
              <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                <button type="button" className="btn btn-secondary" onClick={() => setMov(null)}>Cancelar</button>
                <button type="submit" className="btn btn-primary">Guardar</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
